import { Route, Router } from "@angular/router";

export interface NavItem {
    label: string;
    link: string;
    children: NavItem[];
    hasPage: boolean;
    collapsed: boolean;
    originalLink: string;
    sortOrder: string | number;
}

// This function delegates the search to the external recursive function tofindFirstPageLink().
// The function will always return a NavItem object using the NavItem interface.
export const buildNavArray = (
    routes: Route[],
    basepath: string,
    level: number
): NavItem[] => {

    // Filter the routes that strictly belong to the current level.
    const currentLevelRoutes = routes.filter(route => {
        const validPath =
            basepath === '' || route.path.indexOf(basepath) === 0;

        return (
            route.path.split('/').length === level + 2 &&
            validPath &&
            route.data != null
        );
    });

    // Transform the filtered routes into the navigation structure.
    return currentLevelRoutes
        .map(item => {

            // Recursive call to obtain the children of the next level.
            const children = buildNavArray(
                routes,
                `${item.path}/`,
                level + 1
            ).sort((a, b) => {
                return Number(a.sortOrder) - Number(b.sortOrder);
            });

            const originalLink = `/${item.path}`;
            let link = originalLink;

            // If the node does not have its own page but has children, look for a valid deep link.
            if (!item.data.hasPage && children.length > 0) {
                const deepLink = findFirstPageLink(children);

                // Inherit the link from the first child available in depth.
                if (deepLink) {
                    link = deepLink;
                }
            }

            return {
                label: item.data.label,
                link,
                children,
                hasPage: item.data.hasPage,
                collapsed: true,
                originalLink,
                sortOrder: item.data.sortOrder
            };
        });
};

/**
 * Recursively searches child nav items for the first one that has a page.
 * Used when a parent nav route has no page of its own and needs to redirect
 * to the first valid descendant.
 */
export const findFirstPageLink = (children: any[]): string | null => {
    for (const child of children) {
        if (child.hasPage) {
            return child.link;
        }
        if (child.children && child.children.length > 0) {
            const nested = findFirstPageLink(child.children);
            if (nested) return nested;
        }
    }
    return null;
};

/**
 * Navigates to the specified route with additional handling for dynamic front-end on the RADMAP platform.
 *
 * @param router - The Angular Router service used for navigation.
 * @param link - The target route to navigate to.
 * @returns void
 *
 * @remarks
 * A typical website would use static values in app-routing.module.ts to create routes, but this dynamically
 * generated front-end using dB values on the RADMAP platform requires this workaround to click on a nav button
 * more than once.
 *
 * @example
 * // Example of usage in a component:
 * navigate(this.router, '/desired-route');
 */
export const navigate = (router: Router, link: string): void => {
    //navigate and clear all query params
    //get all query params and set to null
    let queryParams = router.parseUrl(router.url).queryParams;
    for (let key in queryParams) {
        queryParams[key] = null;
    }

    const NavSourcev1 = localStorage.getItem('NavSourcev1');

    // If we are originally from V1, then send through the following variables as well so that the auth logic works
    if (NavSourcev1) {
        queryParams['NavSourceV1'] = NavSourcev1;
        queryParams['sid'] = localStorage.getItem('V1SID');
        queryParams['org_id'] = localStorage.getItem('V1OrgID');
        queryParams['V1UserID'] = localStorage.getItem('V1UserID');
    }

    // Add the workaround to force a navigation
    router.navigate(["/temp-route"]).then(() => {
        router.navigate(["/" + link], {
            queryParams: queryParams,
            queryParamsHandling: "merge"
        });
    });
}
